/**
 * 素ヱコ農園 マルチシップUI
 * カートページで複数配送先の入力を管理する
 * のし種類・表書きはカスタムドロップダウン（太字値 + グレー説明）
 *
 * note_attributes[_multiship_data] に JSON を保存
 *
 * 2026-05-27 改修: 町名フィールド分離 (city と address1 の表記揺れ防止)
 *   - 郵便番号自動補完で town に町域名、address1 は空のまま (顧客は番地のみ入力)
 *   - syncData で address1 = town + ' ' + 番地 結合してサーバーへ
 */
(function () {
  'use strict';

  var container = document.getElementById('MultishipContainer');
  if (!container) return;

  var toggle = document.getElementById('MultishipToggle');
  var toggleLabel = document.getElementById('MultishipToggleLabel');
  var notice = document.getElementById('MultishipNotice');
  var panel = document.getElementById('MultishipPanel');
  var dataInput = document.getElementById('MultishipDataInput');
  var addBtn = document.getElementById('MultishipAddBtn');

  var cartItemsRaw = container.getAttribute('data-cart-items');
  var cartItems = [];
  try {
    cartItems = normalizeCartItems(JSON.parse(cartItemsRaw || '[]'));
  } catch (e) {
    console.warn('[multiship] カートデータパースエラー:', e);
  }

  /**
   * 割り当て対象の明細だけを残す。
   *
   * のし等の「ギフト付属品」は cart-gift-sync.js が独立したカート行として追加する
   * (`properties._gift_accessory = "true"`)。これを数えると、商品1個+のし1個で
   * 合計2個になって複数配送が開いてしまい、付属品には割り当て先が無いので
   * 検証が必ず落ちる。さらにお届け先サマリーに「ギフト付属品 - のし / 蝶結び × 1」が
   * 混ざって顧客のメールとLINEに出てしまう。
   *
   * 子注文ののしは app 側が `giftOptions` から0円明細として作るので、
   * ここで落としても発送物は欠けない。見積もりAPIへ送る cartItems も
   * この配列由来なので、サーバーの数量突合とも一致する。
   */
  function normalizeCartItems(items) {
    return (items || []).filter(function (item) {
      var props = item && item.properties;
      return !(props && String(props._gift_accessory) === 'true');
    });
  }

  // data-* はページ描画時点の値。数量変更はAJAXなので必ず /cart.js で上書きする。
  var cartQuantityTotal = parseInt(container.getAttribute('data-cart-quantity'), 10);
  if (!isFinite(cartQuantityTotal)) cartQuantityTotal = totalQuantityOf(cartItems);
  var cartHasSellingPlan = container.getAttribute('data-has-selling-plan') === 'true';

  var destinations = [];
  var destIndex = 0;

  /** 複数配送に必要な最小個数。1個の商品は2か所へは分けられない。 */
  var MIN_ITEMS_FOR_MULTISHIP = 2;
  /** 下書きを保存するカート属性。`_` 始まりなので顧客向け表示には出ない。 */
  var DRAFT_ATTRIBUTE_KEY = '_multiship_draft';
  var DRAFT_SAVE_DEBOUNCE_MS = 800;

  function totalQuantityOf(items) {
    return (items || []).reduce(function (sum, item) {
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  // --- のし種類 / 表書き ---
  // 商品ページ・カート行UIと同じ定義を使う。単一情報源は gift-options-core.js と
  // 「ギフト付属品」商品の variant metafield (suecoall.gift_option)。
  // 選択肢を増やすときはこのファイルではなく variant 側を編集すること。
  var NOSHI_DESCRIPTIONS = {
    '蝶結び': 'お祝い全般・お中元・お歳暮',
    '結び切り': '結婚祝い・快気祝い',
    '掛け紙（法事用など）': '弔事・法事'
  };

  function giftCore() {
    return window.SuecoGiftCore || null;
  }

  function noshiOptions() {
    var core = giftCore();
    if (!core) return [];
    return core.itemsOfKind('noshi').map(function (item) {
      return { value: item.label, desc: NOSHI_DESCRIPTIONS[item.label] || '' };
    });
  }

  function omotegakiOptions(noshiType) {
    var core = giftCore();
    if (!core || !noshiType) return [];
    return core.omotegakiFor(noshiType);
  }
  // 47都道府県リスト
  var PREFECTURES = [
    { name: '北海道', code: 'JP-01' },
    { name: '青森県', code: 'JP-02' },
    { name: '岩手県', code: 'JP-03' },
    { name: '宮城県', code: 'JP-04' },
    { name: '秋田県', code: 'JP-05' },
    { name: '山形県', code: 'JP-06' },
    { name: '福島県', code: 'JP-07' },
    { name: '茨城県', code: 'JP-08' },
    { name: '栃木県', code: 'JP-09' },
    { name: '群馬県', code: 'JP-10' },
    { name: '埼玉県', code: 'JP-11' },
    { name: '千葉県', code: 'JP-12' },
    { name: '東京都', code: 'JP-13' },
    { name: '神奈川県', code: 'JP-14' },
    { name: '新潟県', code: 'JP-15' },
    { name: '富山県', code: 'JP-16' },
    { name: '石川県', code: 'JP-17' },
    { name: '福井県', code: 'JP-18' },
    { name: '山梨県', code: 'JP-19' },
    { name: '長野県', code: 'JP-20' },
    { name: '岐阜県', code: 'JP-21' },
    { name: '静岡県', code: 'JP-22' },
    { name: '愛知県', code: 'JP-23' },
    { name: '三重県', code: 'JP-24' },
    { name: '滋賀県', code: 'JP-25' },
    { name: '京都府', code: 'JP-26' },
    { name: '大阪府', code: 'JP-27' },
    { name: '兵庫県', code: 'JP-28' },
    { name: '奈良県', code: 'JP-29' },
    { name: '和歌山県', code: 'JP-30' },
    { name: '鳥取県', code: 'JP-31' },
    { name: '島根県', code: 'JP-32' },
    { name: '岡山県', code: 'JP-33' },
    { name: '広島県', code: 'JP-34' },
    { name: '山口県', code: 'JP-35' },
    { name: '徳島県', code: 'JP-36' },
    { name: '香川県', code: 'JP-37' },
    { name: '愛媛県', code: 'JP-38' },
    { name: '高知県', code: 'JP-39' },
    { name: '福岡県', code: 'JP-40' },
    { name: '佐賀県', code: 'JP-41' },
    { name: '長崎県', code: 'JP-42' },
    { name: '熊本県', code: 'JP-43' },
    { name: '大分県', code: 'JP-44' },
    { name: '宮崎県', code: 'JP-45' },
    { name: '鹿児島県', code: 'JP-46' },
    { name: '沖縄県', code: 'JP-47' }
  ];

  // --- 複数配送を提供できるカートかどうか ---
  //
  // 使えない理由は必ず文言で返す。トグルを黙って無効化すると、顧客は
  // 「複数配送が見当たらない」としか分からず問い合わせになる。
  function multishipBlockReason() {
    if (cartHasSellingPlan) {
      return '定期便の商品がカートに入っているため、複数のお届け先には分けられません。'
        + '定期便は毎回同じお届け先へお送りします。'
        + 'ギフトを複数のお届け先へ送る場合は、定期便とは分けてご注文ください。';
    }
    if (cartQuantityTotal < MIN_ITEMS_FOR_MULTISHIP) {
      return '複数のお届け先に分けるには、商品が' + MIN_ITEMS_FOR_MULTISHIP + '個以上必要です。'
        + '数量を増やすか、商品を追加してください。';
    }
    return null;
  }

  /**
   * 複数配送中はウォレット決済(Shop Pay等)を塞ぐ。
   *
   * ウォレットボタンは main-cart-footer セクションにあり `<form id="cart">` の外側なので、
   * このスクリプトの submit ハンドラを一切通らない。そこから決済すると
   * `_multiship_data` も お届け先N も書かれず、顧客は2件に分けたつもりで
   * 1か所にだけ届く。複数配送を使う人にだけ通常の「ご購入手続きへ」を強制する。
   */
  function setDynamicCheckoutBlocked(blocked) {
    var wallets = document.querySelectorAll('.cart__dynamic-checkout-buttons');
    for (var i = 0; i < wallets.length; i++) {
      wallets[i].classList.toggle('multiship-wallet-blocked', !!blocked);
    }
    var hint = document.getElementById('MultishipCheckoutHint');
    if (hint) hint.hidden = !blocked;
  }

  /** 可否をUIへ反映する。使えなくなった場合は入力中の配送先も畳む。 */
  function updateAvailability() {
    var reason = multishipBlockReason();
    var blocked = reason !== null;

    if (toggle) toggle.disabled = blocked;
    if (toggleLabel) toggleLabel.classList.toggle('multiship-toggle--disabled', blocked);
    if (notice) {
      notice.hidden = !blocked;
      notice.textContent = reason || '';
    }

    if (blocked && toggle && toggle.checked) {
      toggle.checked = false;
      closeMultiship();
    }
    // ウォレット決済の遮断は「複数配送が有効かどうか」ではなく
    // 「いま複数配送を使っているか」で決める。単一配送の顧客の決済導線は変えない。
    setDynamicCheckoutBlocked(!blocked && !!(toggle && toggle.checked));
    return !blocked;
  }

  /** 複数配送をやめたときの後始末。カート側に残る痕跡もすべて消す。 */
  function closeMultiship() {
    destinations = [];
    destIndex = 0;
    if (panel) panel.hidden = true;
    if (draftSaveTimer) {
      clearTimeout(draftSaveTimer);
      draftSaveTimer = null;
    }
    renderDestinations();
    syncData();
    // hidden inputは空値で残し、フォーム送信経路でも確実に消えるようにする。
    blankDestinationSummaryInputs();
    setDynamicCheckoutBlocked(false);
    clearMultishipCart();
  }

  // --- トグル ---
  if (toggle) {
    toggle.addEventListener('change', function () {
      if (this.checked && !updateAvailability()) return;

      panel.hidden = !this.checked;
      if (!this.checked) {
        closeMultiship();
        return;
      }

      // 開いた時点のカートを正とする。data-cart-items は描画時の値で古い。
      setDynamicCheckoutBlocked(true);
      refreshCartState().then(function () {
        if (!updateAvailability()) return;
        if (destinations.length === 0) {
          var initial = Math.min(2, cartQuantityTotal);
          for (var i = 0; i < initial; i++) addDestination();
          syncData();
        }
      });
    });
  }

  if (addBtn) {
    addBtn.addEventListener('click', function () {
      addDestination();
    });
  }

  // --- カート追従 ---
  //
  // 数量変更は cart.js の AJAX で行われ、差し替え対象は `.js-contents` だけ。
  // このフォームはその外側にあるため data-cart-items は永久に古いままになる。
  // 古い個数のまま見積もると、サーバーが quantity_mismatch を返して
  // チェックアウトできない。カートが動いたら必ず取り直す。
  function refreshCartState() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(assertOk)
      .then(function (response) { return response.json(); })
      .then(function (cart) { applyCartState(cart.items || []); return true; })
      .catch(function (err) {
        console.warn('[multiship] カート状態の再取得に失敗:', err);
        return false;
      });
  }

  function applyCartState(items) {
    cartItems = normalizeCartItems(items).map(function (item) {
      return {
        variant_id: item.variant_id,
        quantity: item.quantity,
        title: item.title,
        price: item.price
      };
    });
    cartQuantityTotal = totalQuantityOf(cartItems);
    cartHasSellingPlan = items.some(function (item) {
      return !!item.selling_plan_allocation;
    });

    reconcileDestinationLineItems();
    updateRemainingQty();
    renderDestinations();
    syncData();
    updateAvailability();
  }

  /**
   * カートの中身が変わったとき、各配送先の明細を現在のカートに合わせ直す。
   * 既存の割り当ては可能な範囲で残し、在庫が減った分だけ切り詰める。
   * 入力済みの住所は触らない。
   */
  function reconcileDestinationLineItems() {
    destinations.forEach(function (dest) {
      dest.lineItems = cartItems.map(function (item) {
        var vid = String(item.variant_id);
        var existing = (dest.lineItems || []).find(function (li) {
          return li.variantId === vid;
        });
        return {
          variantId: vid,
          title: item.title,
          price: String(item.price / 100),
          quantity: Math.min(existing ? existing.quantity : 0, item.quantity),
          maxQty: item.quantity
        };
      });
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      refreshCartState();
    });
  }

  function addDestination() {
    var dest = {
      id: destIndex++,
      firstName: '',
      lastName: '',
      town: '',
      address1: '',
      address2: '',
      city: '',
      province: '',
      zip: '',
      countryCode: 'JP',
      phone: '',
      lineItems: cartItems.map(function (item) {
        return {
          variantId: String(item.variant_id),
          title: item.title,
          price: String(item.price / 100),
          quantity: 0,
          maxQty: item.quantity
        };
      }),
      giftOptions: {}
    };
    destinations.push(dest);
    updateRemainingQty();
    renderDestinations();
    // 追加した直後に離脱しても下書きが残るようにする。
    syncData();
  }

  function removeDestination(id) {
    destinations = destinations.filter(function (d) { return d.id !== id; });
    updateRemainingQty();
    renderDestinations();
    syncData();
  }

  function updateRemainingQty() {
    cartItems.forEach(function (item) {
      var vid = String(item.variant_id);
      var totalAllocated = destinations.reduce(function (sum, d) {
        var li = d.lineItems.find(function (l) { return l.variantId === vid; });
        return sum + (li ? li.quantity : 0);
      }, 0);
      var remaining = item.quantity - totalAllocated;
      destinations.forEach(function (d) {
        var li = d.lineItems.find(function (l) { return l.variantId === vid; });
        if (li) {
          li.maxQty = li.quantity + Math.max(0, remaining);
        }
      });
    });
  }

  function buildPrefectureSelect(destId, selected) {
    var html = '<select data-dest="' + destId + '" data-field="province" style="width:100%;padding:8px;border:1px solid #ccc;border-radius:4px;font-size:14px;">';
    html += '<option value="">選択してください</option>';
    PREFECTURES.forEach(function (p) {
      html += '<option value="' + p.code + '"' + (selected === p.code ? ' selected' : '') + '>' + p.name + '</option>';
    });
    html += '</select>';
    return html;
  }

  // --- カスタムドロップダウンHTML生成 ---
  function buildCustomSelect(destId, fieldName, label, options, selectedValue, placeholder) {
    var displayText = placeholder || '選択してください';
    if (selectedValue) {
      var found = options.find(function(o) { return o.value === selectedValue; });
      if (found) displayText = found.value;
    }

    var html = '<div class="multiship-field">' +
      '<label>' + label + '</label>' +
      '<div class="ms-custom-select" data-ms-dropdown data-dest-id="' + destId + '" data-field-name="' + fieldName + '">' +
        '<button type="button" class="ms-custom-select__btn" data-ms-btn>' +
          '<span data-ms-label>' + esc(displayText) + '</span>' +
          '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 5l3 3 3-3"/></svg>' +
        '</button>' +
        '<ul class="ms-custom-select__list" data-ms-list hidden>';

    options.forEach(function(opt) {
      html += '<li class="ms-custom-select__item' + (opt.value === selectedValue ? ' ms-custom-select__item--selected' : '') + '" data-value="' + esc(opt.value) + '">' +
        '<span class="ms-custom-select__item-value">' + esc(opt.value) + '</span>' +
        '<span class="ms-custom-select__item-desc">（' + esc(opt.desc) + '）</span>' +
      '</li>';
    });

    html += '</ul></div></div>';
    return html;
  }

  // --- レンダリング ---
  function renderDestinations() {
    var listEl = document.getElementById('MultishipList');
    if (!listEl) return;
    listEl.innerHTML = '';

    destinations.forEach(function (dest, idx) {
      var card = document.createElement('div');
      card.className = 'multiship-dest';
      card.innerHTML =
        '<div class="multiship-dest__header">' +
          '<span class="multiship-dest__label">配送先 ' + (idx + 1) + '</span>' +
          // 2件でも削除できるようにする。以前は3件以上でしか出さなかったため、
          // 商品1個のカートでトグルを入れると割り当て不能な2件が残り、
          // トグルを戻して入力を全部捨てる以外に逃げ道がなかった。
          (destinations.length > 1 ? '<button type="button" class="multiship-dest__remove" data-dest-id="' + dest.id + '">削除</button>' : '') +
        '</div>' +
        '<div class="multiship-row">' +
          '<div class="multiship-field">' +
            '<label>姓 <span class="multiship-req">*</span></label>' +
            '<input type="text" data-dest="' + dest.id + '" data-field="lastName" value="' + esc(dest.lastName) + '" placeholder="山田">' +
          '</div>' +
          '<div class="multiship-field">' +
            '<label>名 <span class="multiship-req">*</span></label>' +
            '<input type="text" data-dest="' + dest.id + '" data-field="firstName" value="' + esc(dest.firstName) + '" placeholder="太郎">' +
          '</div>' +
        '</div>' +
        '<div class="multiship-field">' +
          '<label>郵便番号 <span class="multiship-req">*</span></label>' +
          '<input type="text" data-dest="' + dest.id + '" data-field="zip" value="' + esc(dest.zip) + '" placeholder="123-4567" autocomplete="postal-code">' +
          '<small style="color:#888;font-size:11px;">7桁入力で都道府県・市区町村・町名を自動入力します</small>' +
        '</div>' +
        '<div class="multiship-field">' +
          '<label>都道府県 <span class="multiship-req">*</span></label>' +
          buildPrefectureSelect(dest.id, dest.province) +
        '</div>' +
        '<div class="multiship-field">' +
          '<label>市区町村 <span class="multiship-req">*</span></label>' +
          '<input type="text" data-dest="' + dest.id + '" data-field="city" value="' + esc(dest.city) + '" placeholder="例: 四街道市">' +
          '<small style="color:#888;font-size:11px;">市区町までのみ。町域名は次の「町名」へ</small>' +
        '</div>' +
        '<div class="multiship-field">' +
          '<label>町名 <span class="multiship-req">*</span></label>' +
          '<input type="text" data-dest="' + dest.id + '" data-field="town" value="' + esc(dest.town) + '" placeholder="例: 和良比">' +
          '<small style="color:#888;font-size:11px;">市区町村以下の町域名 (自動入力後の編集可)</small>' +
        '</div>' +
        '<div class="multiship-field">' +
          '<label>番地 <span class="multiship-req">*</span></label>' +
          '<input type="text" data-dest="' + dest.id + '" data-field="address1" value="' + esc(dest.address1) + '" placeholder="例: 826-10">' +
        '</div>' +
        '<div class="multiship-field">' +
          '<label>建物名・部屋番号（任意）</label>' +
          '<input type="text" data-dest="' + dest.id + '" data-field="address2" value="' + esc(dest.address2) + '" placeholder="">' +
        '</div>' +
        '<div class="multiship-field">' +
          '<label>電話番号</label>' +
          '<input type="text" data-dest="' + dest.id + '" data-field="phone" value="' + esc(dest.phone) + '" placeholder="090-1234-5678">' +
        '</div>' +
        buildItemsHtml(dest) +
        buildGiftHtml(dest);

      listEl.appendChild(card);
    });

    bindFieldEvents(listEl);
    bindQtyEvents(listEl);
    bindRemoveEvents(listEl);
    bindGiftEvents(listEl);
    bindCustomSelectEvents(listEl);
    bindZipAutoComplete(listEl);
  }

  function buildItemsHtml(dest) {
    if (cartItems.length === 0) return '';
    var html = '<div class="multiship-items">' +
      '<div class="multiship-items__title">商品の割り当て</div>';
    dest.lineItems.forEach(function (li) {
      html += '<div class="multiship-item-row">' +
        '<span>' + esc(li.title) + '</span>' +
        '<input type="number" min="0" max="' + li.maxQty + '" value="' + li.quantity + '" data-dest-qty="' + dest.id + '" data-variant="' + li.variantId + '">' +
        '<span>/ ' + li.maxQty + '</span>' +
      '</div>';
    });
    html += '</div>';
    return html;
  }

  function buildGiftHtml(dest) {
    var go = dest.giftOptions || {};

    // 表書きオプション（のし選択に依存）
    var omotegakiOpts = omotegakiOptions(go.noshi);

    return '<details class="multiship-gift">' +
      '<summary>この配送先のギフトオプション</summary>' +
      '<div class="multiship-gift__body">' +

        buildCustomSelect(dest.id, 'noshi', 'のしの種類', noshiOptions(), go.noshi || '', '選択してください') +
        '<div class="multiship-field">' +
          '<label>のしの配置</label>' +
          '<select data-gift-dest="' + dest.id + '" data-gift-field="noshiPlacement">' +
            '<option value="外のし"' + (go.noshiPlacement === '外のし' ? ' selected' : '') + '>外のし（手渡し向け）</option>' +
            '<option value="内のし"' + ((go.noshiPlacement === '内のし' || !go.noshiPlacement) ? ' selected' : '') + '>内のし（配送向け）</option>' +
          '</select>' +
        '</div>' +
        buildCustomSelect(dest.id, 'omoteGaki', '表書き', omotegakiOpts, go.omoteGaki || '', go.noshi ? '選択してください' : 'のしの種類を先に選択してください') +
        '<div class="multiship-field">' +
          '<label>名入れ</label>' +
          '<input type="text" data-gift-dest="' + dest.id + '" data-gift-field="naire" value="' + esc(go.naire || '') + '" placeholder="例: 山田太郎">' +
        '</div>' +
      '</div>' +
    '</details>';
  }

  // --- イベントバインド ---
  function bindFieldEvents(listEl) {
    listEl.querySelectorAll('[data-dest][data-field]').forEach(function (input) {
      function handler() {
        var destId = parseInt(this.getAttribute('data-dest'));
        var field = this.getAttribute('data-field');
        var dest = destinations.find(function (d) { return d.id === destId; });
        if (dest) {
          dest[field] = this.value;
          syncData();
        }
      }
      input.addEventListener('input', handler);
      input.addEventListener('change', handler);
    });
  }

  function bindQtyEvents(listEl) {
    listEl.querySelectorAll('[data-dest-qty]').forEach(function (input) {
      input.addEventListener('input', function () {
        var destId = parseInt(this.getAttribute('data-dest-qty'));
        var variantId = this.getAttribute('data-variant');
        var dest = destinations.find(function (d) { return d.id === destId; });
        if (dest) {
          var li = dest.lineItems.find(function (l) { return l.variantId === variantId; });
          if (li) {
            li.quantity = Math.max(0, parseInt(this.value) || 0);
            updateRemainingQty();
            listEl.querySelectorAll('[data-dest-qty]').forEach(function (inp) {
              var dId = parseInt(inp.getAttribute('data-dest-qty'));
              var vId = inp.getAttribute('data-variant');
              var d = destinations.find(function (dd) { return dd.id === dId; });
              if (d) {
                var item = d.lineItems.find(function (l) { return l.variantId === vId; });
                if (item) inp.setAttribute('max', item.maxQty);
              }
            });
            syncData();
          }
        }
      });
    });
  }

  function bindRemoveEvents(listEl) {
    listEl.querySelectorAll('.multiship-dest__remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        removeDestination(parseInt(this.getAttribute('data-dest-id')));
      });
    });
  }

  function bindGiftEvents(listEl) {
    listEl.querySelectorAll('[data-gift-field]').forEach(function (input) {
      function handler() {
        var destId = parseInt(this.getAttribute('data-gift-dest'));
        var field = this.getAttribute('data-gift-field');
        var dest = destinations.find(function (d) { return d.id === destId; });
        if (dest) {
          if (!dest.giftOptions) dest.giftOptions = {};
          dest.giftOptions[field] = this.type === 'checkbox' ? this.checked : this.value;
          syncData();
        }
      }
      input.addEventListener('input', handler);
      input.addEventListener('change', handler);
    });
  }

  // --- カスタムドロップダウンのイベント ---
  function bindCustomSelectEvents(listEl) {
    listEl.querySelectorAll('[data-ms-dropdown]').forEach(function (dropdown) {
      var btn = dropdown.querySelector('[data-ms-btn]');
      var list = dropdown.querySelector('[data-ms-list]');
      var label = dropdown.querySelector('[data-ms-label]');
      var destId = parseInt(dropdown.getAttribute('data-dest-id'));
      var fieldName = dropdown.getAttribute('data-field-name');

      if (!btn || !list) return;

      // ボタンクリックで開閉
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        // 他のドロップダウンを閉じる
        closeAllDropdowns(dropdown);
        var isOpen = !list.hidden;
        list.hidden = isOpen;
        btn.setAttribute('aria-expanded', String(!isOpen));
      });

      // リストアイテムのクリック
      list.querySelectorAll('.ms-custom-select__item').forEach(function (li) {
        li.addEventListener('click', function (e) {
          e.stopPropagation();
          var value = this.getAttribute('data-value');

          // ラベル更新
          if (label) label.textContent = value;

          // 選択状態
          list.querySelectorAll('.ms-custom-select__item').forEach(function (el) {
            el.classList.remove('ms-custom-select__item--selected');
          });
          this.classList.add('ms-custom-select__item--selected');

          // 閉じる
          list.hidden = true;
          btn.setAttribute('aria-expanded', 'false');

          // データ更新
          var dest = destinations.find(function (d) { return d.id === destId; });
          if (dest) {
            if (!dest.giftOptions) dest.giftOptions = {};
            dest.giftOptions[fieldName] = value;

            // のしの種類が変わったら表書きをリセットして再描画
            if (fieldName === 'noshi') {
              dest.giftOptions.omoteGaki = '';
              renderDestinations();
              // detailsを開いた状態に戻す
              var destCards = listEl.querySelectorAll('.multiship-dest');
              destCards.forEach(function (card, idx) {
                if (destinations[idx] && destinations[idx].id === destId) {
                  var details = card.querySelector('details.multiship-gift');
                  if (details) details.setAttribute('open', '');
                }
              });
            }
            syncData();
          }
        });
      });
    });
  }

  // --- 郵便番号から住所自動入力 ---
  // 都道府県名 → Shopify province code のマッピング
  var PREF_NAME_TO_CODE = {};
  PREFECTURES.forEach(function (p) { PREF_NAME_TO_CODE[p.name] = p.code; });

  // Shopify province code → 都道府県名 (顧客向け表示で使う)
  var PREF_CODE_TO_NAME = {};
  PREFECTURES.forEach(function (p) { PREF_CODE_TO_NAME[p.code] = p.name; });

  function bindZipAutoComplete(listEl) {
    listEl.querySelectorAll('[data-field="zip"]').forEach(function (input) {
      input.addEventListener('input', function () {
        var destId = parseInt(this.getAttribute('data-dest'));
        var raw = this.value.replace(/[^0-9]/g, '');
        if (raw.length === 7) {
          lookupZip(raw, destId, listEl);
        }
      });
    });
  }

  function lookupZip(zipcode, destId, listEl) {
    fetch('https://zipcloud.ibsnet.co.jp/api/search?zipcode=' + zipcode)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data.results || data.results.length === 0) return;
        var result = data.results[0];
        var dest = destinations.find(function (d) { return d.id === destId; });
        if (!dest) return;

        // 都道府県を設定
        var prefCode = PREF_NAME_TO_CODE[result.address1] || '';
        dest.province = prefCode;

        // 市区町村を設定 (例: 四街道市)
        dest.city = result.address2 || '';

        // 町名を独立フィールドに設定 (例: 和良比)
        // 番地 (address1) は顧客が手入力する想定で空のまま
        dest.town = result.address3 || '';

        // UI を更新（再レンダリングせずDOM直接更新でフォーカス維持）
        var prefSelect = listEl.querySelector('[data-dest="' + destId + '"][data-field="province"]');
        if (prefSelect) prefSelect.value = prefCode;

        var cityInput = listEl.querySelector('[data-dest="' + destId + '"][data-field="city"]');
        if (cityInput) cityInput.value = dest.city;

        var townInput = listEl.querySelector('[data-dest="' + destId + '"][data-field="town"]');
        if (townInput) townInput.value = dest.town;

        // 番地フィールドにフォーカスを移す（顧客が番地を入力するため）
        var addr1Input = listEl.querySelector('[data-dest="' + destId + '"][data-field="address1"]');
        if (addr1Input) {
          addr1Input.focus();
        }

        syncData();
      })
      .catch(function (err) {
        console.warn('郵便番号検索エラー:', err);
      });
  }

  // --- 全カスタムドロップダウンを閉じる ---
  function closeAllDropdowns(except) {
    document.querySelectorAll('[data-ms-dropdown]').forEach(function (dd) {
      if (dd === except) return;
      var list = dd.querySelector('[data-ms-list]');
      var btn = dd.querySelector('[data-ms-btn]');
      if (list) list.hidden = true;
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  // 外側クリックで閉じる
  document.addEventListener('click', function () {
    closeAllDropdowns(null);
  });

  // --- データ同期 ---
  function syncData() {
    if (!dataInput) return;
    var data = {
      version: 1,
      destinations: destinations.map(function (dest, idx) {
        // 町名と番地をサーバー送信時に address1 として結合
        // (Shopify shippingAddress 慣例: address1 に町名+番地、city に市区町村のみ)
        var combinedAddress1 = [dest.town, dest.address1]
          .map(function (s) { return String(s || '').trim(); })
          .filter(Boolean)
          .join(' ');
        return {
          index: idx,
          firstName: dest.firstName,
          lastName: dest.lastName,
          address1: combinedAddress1,
          address2: dest.address2,
          city: dest.city,
          province: dest.province,
          zip: dest.zip,
          countryCode: dest.countryCode,
          phone: dest.phone,
          lineItems: dest.lineItems.filter(function (li) { return li.quantity > 0; }).map(function (li) {
            return { variantId: li.variantId, quantity: li.quantity, price: li.price };
          }),
          giftOptions: dest.giftOptions || {}
        };
      })
    };
    dataInput.value = JSON.stringify(data);
    scheduleDraftSave();
  }

  // --- 入力途中の下書き保存 ---
  //
  // 配送先はこれまでJSのメモリ上にしか無く、カートへ書かれるのは
  // チェックアウト送信の瞬間だけだった。リロード・戻る・別ページ経由で
  // 入力済みの住所がすべて消えるため、入力のたびにカート属性へ退避する。
  //
  // syncData が出す `_multiship_data` は町名と番地を結合してしまい入力欄へ
  // 戻せないので、復元用には画面の状態をそのまま持つ別キーを使う。
  var draftSaveTimer = null;
  var draftExists = !!container.getAttribute('data-draft');

  function draftPayload(list) {
    return (list || []).map(function (dest) {
      return {
        firstName: dest.firstName,
        lastName: dest.lastName,
        town: dest.town,
        address1: dest.address1,
        address2: dest.address2,
        city: dest.city,
        province: dest.province,
        zip: dest.zip,
        countryCode: dest.countryCode || 'JP',
        phone: dest.phone,
        lineItems: (dest.lineItems || []).map(function (li) {
          return { variantId: li.variantId, quantity: li.quantity };
        }),
        giftOptions: dest.giftOptions || {}
      };
    });
  }

  function scheduleDraftSave() {
    // 配送先も過去の下書きも無いカートで、ページを開くたびに書き込まない。
    if (destinations.length === 0 && !draftExists) return;
    if (draftSaveTimer) clearTimeout(draftSaveTimer);
    draftSaveTimer = setTimeout(function () {
      draftSaveTimer = null;
      saveDraftNow(destinations);
    }, DRAFT_SAVE_DEBOUNCE_MS);
  }

  /** 下書きの保存失敗で購入を止めない。あくまで取りこぼし防止の保険。 */
  function saveDraftNow(list) {
    draftExists = !!(list && list.length);
    var attributes = {};
    attributes[DRAFT_ATTRIBUTE_KEY] = list && list.length
      ? JSON.stringify(draftPayload(list))
      : '';
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).catch(function (err) {
      console.warn('[multiship] 下書きの保存に失敗:', err);
    });
  }

  /** ページ描画時にカート属性から入力を復元する。 */
  function restoreDraft() {
    var raw = container.getAttribute('data-draft');
    if (!raw) return false;

    var saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      console.warn('[multiship] 下書きのパースに失敗:', e);
      return false;
    }
    if (!Array.isArray(saved) || saved.length === 0) return false;

    destinations = saved.map(function (dest) {
      return {
        id: destIndex++,
        firstName: dest.firstName || '',
        lastName: dest.lastName || '',
        town: dest.town || '',
        address1: dest.address1 || '',
        address2: dest.address2 || '',
        city: dest.city || '',
        province: dest.province || '',
        zip: dest.zip || '',
        countryCode: dest.countryCode || 'JP',
        phone: dest.phone || '',
        lineItems: [],
        giftOptions: dest.giftOptions || {}
      };
    });

    // 明細は保存時のカートではなく現在のカートに合わせ直す。
    saved.forEach(function (savedDest, idx) {
      destinations[idx].lineItems = (savedDest.lineItems || []).map(function (li) {
        return { variantId: li.variantId, quantity: li.quantity };
      });
    });
    reconcileDestinationLineItems();
    return true;
  }

  // --- 顧客向けお届け先サマリー ---
  // `_multiship_data` はJSONなので、Liquid (注文確定メール) も
  // customer-account拡張もそのままでは読めない。
  // 同じ内容を日本の住所表記のまま平文属性へ複製し、
  // 「マルチシップのときだけ複数お届け先を表示する」の唯一の入力にする。
  // 読み手: 注文確定メールテンプレート / extensions/delivery-date-display
  var DEST_SUMMARY_COUNT_KEY = 'お届け先件数';
  var DEST_SUMMARY_PREFIX = 'お届け先';
  var DEST_SUMMARY_FIELD_SEPARATOR = '｜';
  var DEST_SUMMARY_MAX_LENGTH = 500;

  function isDestinationSummaryKey(key) {
    if (key === DEST_SUMMARY_COUNT_KEY) return true;
    return /^お届け先[0-9]+$/.test(String(key || ''));
  }

  function formatJapaneseZip(zip) {
    var digits = String(zip || '').replace(/[^0-9]/g, '');
    if (digits.length !== 7) return String(zip || '').trim();
    return '〒' + digits.slice(0, 3) + '-' + digits.slice(3);
  }

  // 姓 名 様 (日本語の氏名は姓が先)
  function formatJapaneseRecipient(dest) {
    var name = [dest.lastName, dest.firstName]
      .map(function (s) { return String(s || '').trim(); })
      .filter(Boolean)
      .join(' ');
    return name ? name + ' 様' : '';
  }

  // 〒464-0011 愛知県名古屋市千種区千代田橋1-1-9 千代田橋ガーデンA棟303
  // 都道府県・市区町村・町名・番地は詰めて書き、建物名だけ空白で区切る。
  function formatJapaneseAddress(dest) {
    var street = [
      PREF_CODE_TO_NAME[dest.province] || '',
      dest.city,
      dest.town,
      dest.address1
    ].map(function (s) { return String(s || '').trim(); }).join('');

    return [formatJapaneseZip(dest.zip), street, String(dest.address2 || '').trim()]
      .filter(Boolean)
      .join(' ');
  }

  function formatDestinationItems(dest) {
    return (dest.lineItems || [])
      .filter(function (li) { return li.quantity > 0; })
      .map(function (li) { return li.title + ' × ' + li.quantity; })
      .join('、');
  }

  function formatDestinationSummaryLine(dest) {
    return [
      formatJapaneseRecipient(dest),
      formatJapaneseAddress(dest),
      String(dest.phone || '').trim(),
      formatDestinationItems(dest)
    ]
      .filter(Boolean)
      .join(DEST_SUMMARY_FIELD_SEPARATOR)
      .slice(0, DEST_SUMMARY_MAX_LENGTH);
  }

  function buildDestinationSummaryAttributes() {
    var attributes = {};
    if (!toggle || !toggle.checked || destinations.length === 0) return attributes;

    attributes[DEST_SUMMARY_COUNT_KEY] = String(destinations.length);
    destinations.forEach(function (dest, idx) {
      attributes[DEST_SUMMARY_PREFIX + (idx + 1)] = formatDestinationSummaryLine(dest);
    });
    return attributes;
  }

  // --- バリデーション ---
  function validateBeforeCheckout() {
    if (!toggle || !toggle.checked) return true;
    var errors = [];

    destinations.forEach(function (dest, idx) {
      var label = '配送先 ' + (idx + 1);
      if (!dest.lastName) errors.push(label + ': 姓が未入力です');
      if (!dest.firstName) errors.push(label + ': 名が未入力です');
      if (!dest.zip) {
        errors.push(label + ': 郵便番号が未入力です');
      } else if (!/^\d{3}-?\d{4}$/.test(dest.zip)) {
        errors.push(label + ': 郵便番号の形式が正しくありません（例: 123-4567）');
      }
      if (!dest.province) errors.push(label + ': 都道府県が未選択です');
      if (!dest.city) errors.push(label + ': 市区町村が未入力です');
      if (!dest.town) errors.push(label + ': 町名が未入力です');
      if (!dest.address1) errors.push(label + ': 番地が未入力です');

      var totalQty = dest.lineItems.reduce(function (sum, li) { return sum + li.quantity; }, 0);
      if (totalQty === 0) errors.push(label + ': 商品が割り当てられていません');
    });

    cartItems.forEach(function (item) {
      var allocated = destinations.reduce(function (sum, dest) {
        var li = dest.lineItems.find(function (l) { return l.variantId === String(item.variant_id); });
        return sum + (li ? li.quantity : 0);
      }, 0);
      if (allocated !== item.quantity) {
        errors.push(item.title + ': 割り当て数(' + allocated + ')がカート数(' + item.quantity + ')と一致しません');
      }
    });

    if (errors.length > 0) {
      alert('以下の問題を修正してください:\n\n' + errors.join('\n'));
      return false;
    }
    return true;
  }

  var cartForm = document.getElementById('cart');
  var quoteSubmitInProgress = false;
  var allowQuotedSubmit = false;
  if (cartForm) {
    cartForm.addEventListener('submit', function (e) {
      if (!validateBeforeCheckout()) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (!toggle || !toggle.checked) return;
      if (allowQuotedSubmit) {
        allowQuotedSubmit = false;
        return;
      }

      e.preventDefault();
      if (quoteSubmitInProgress) return;

      createQuoteAndSubmit(e.submitter || document.activeElement);
    });
  }

  async function createQuoteAndSubmit(submitter) {
    try {
      quoteSubmitInProgress = true;
      setSubmitting(true);
      // 直前にカートが動いていても、送るのは必ず現在のカートに揃えた内容にする。
      await refreshCartState();
      if (!updateAvailability()) return;
      if (!validateBeforeCheckout()) return;
      syncData();
      var multishipData = readMultishipData();
      var quote = await createShippingQuote(multishipData);
      await persistQuoteToCart(multishipData, quote);
      setQuoteHiddenFields(multishipData, quote);

      allowQuotedSubmit = true;
      setSubmitting(false);
      if (submitter && typeof submitter.click === 'function') {
        submitter.click();
      } else if (cartForm && cartForm.requestSubmit) {
        cartForm.requestSubmit();
      } else if (cartForm) {
        cartForm.submit();
      }
    } catch (err) {
      console.error('[multiship] 送料見積もり失敗:', err);
      // サーバーは quantity_mismatch のような具体的な理由を返している。
      // 以前はそれを捨てて「時間を置いて再度お試しください」とだけ出しており、
      // 待っても直らない問題を待たせていた。原因と対処をそのまま伝える。
      alert(quoteErrorMessage(err));
    } finally {
      quoteSubmitInProgress = false;
      setSubmitting(false);
    }
  }

  /** サーバーが返した理由を、顧客がその場で直せる文言にする。 */
  function quoteErrorMessage(err) {
    var reason = err && err.reason;
    var retryLater = '複数配送先の送料を計算できませんでした。時間を置いて再度お試しください。';

    if (reason === 'quantity_mismatch') {
      return '配送先へ割り当てた個数の合計が、カートの個数と一致していません。'
        + '\n各配送先の個数を見直すか、カートの数量を変更してください。';
    }
    if (reason === 'over_limit') {
      return '1つの配送先にまとめられる個数の上限を超えています。'
        + '\n配送先を増やして分けてください。';
    }
    if (reason === 'invalid_multiship_data') {
      return '配送先の入力内容に不備があります。\n未入力の項目がないかご確認ください。';
    }
    if (reason === 'missing_multiship_data') {
      return '配送先が入力されていません。';
    }
    if (reason === 'missing_rate') {
      return '現在、送料を計算できませんでした。時間を置いて再度お試しください。'
        + '\n繰り返す場合はLINEでお問い合わせください。';
    }
    if (err && err.serverMessage) return String(err.serverMessage);
    return retryLater;
  }

  function readMultishipData() {
    if (!dataInput || !dataInput.value) {
      throw new Error('missing_multiship_data');
    }
    return JSON.parse(dataInput.value);
  }

  async function createShippingQuote(multishipData) {
    var response = await fetch('/apps/documents/multiship/quote', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        multishipData: multishipData,
        cartItems: cartItems.map(function (item) {
          return {
            variantId: String(item.variant_id),
            quantity: item.quantity
          };
        })
      })
    });
    var body = await response.json().catch(function () { return null; });
    if (!response.ok || !body || !body.ok) {
      var quoteError = new Error((body && body.reason) || 'quote_failed');
      quoteError.reason = (body && body.reason) || 'quote_failed';
      quoteError.serverMessage = body && body.message;
      throw quoteError;
    }
    return body;
  }

  async function persistQuoteToCart(multishipData, quote) {
    // 配送先を減らした場合に古い お届け先N が残らないよう、
    // 先にカートの現状を読んで既存サマリーを消してから書き直す。
    var cart = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    }).then(assertOk).then(function (response) { return response.json(); });

    var attributes = {};
    Object.keys(cart.attributes || {}).forEach(function (key) {
      if (isDestinationSummaryKey(key)) attributes[key] = '';
    });

    // 確定した内容は `_multiship_data` が持つので、入力途中の下書きは注文に残さない。
    attributes[DRAFT_ATTRIBUTE_KEY] = '';
    draftExists = false;
    attributes['_multiship_data'] = JSON.stringify(multishipData);
    attributes['_multiship_carrier_ready'] = '1';
    attributes['_multiship_quote_id'] = quote.quoteId;
    attributes['_multiship_quote_amount'] = String(quote.amountYen);
    attributes['_multiship_quote_currency'] = quote.currencyCode || 'JPY';

    var summary = buildDestinationSummaryAttributes();
    Object.keys(summary).forEach(function (key) {
      attributes[key] = summary[key];
    });

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(assertOk);

    for (var i = cart.items.length - 1; i >= 0; i--) {
      var item = cart.items[i];
      var properties = copyProperties(item.properties);
      properties['_multiship_quote_id'] = quote.quoteId;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          line: i + 1,
          quantity: item.quantity,
          properties: properties
        })
      }).then(assertOk);
    }
  }

  function setQuoteHiddenFields(multishipData, quote) {
    if (dataInput) dataInput.value = JSON.stringify(multishipData);
    setCartAttributeInput('_multiship_carrier_ready', '1');
    setCartAttributeInput('_multiship_quote_id', quote.quoteId);
    setCartAttributeInput('_multiship_quote_amount', String(quote.amountYen));
    setCartAttributeInput('_multiship_quote_currency', quote.currencyCode || 'JPY');

    // 配送先を減らした直後に古い hidden input が残ると、
    // カート側で消した お届け先N がフォーム送信で復活する。先に取り除く。
    removeDestinationSummaryInputs();
    var summary = buildDestinationSummaryAttributes();
    Object.keys(summary).forEach(function (key) {
      setCartAttributeInput(key, summary[key]);
    });
  }

  function removeDestinationSummaryInputs() {
    forEachDestinationSummaryInput(function (input) { input.remove(); });
  }

  // 空値のhidden inputはフォーム送信でその属性を消す。
  // 複数配送をやめた直後は、消したいので値を空にして残す。
  function blankDestinationSummaryInputs() {
    forEachDestinationSummaryInput(function (input) { input.value = ''; });
  }

  function forEachDestinationSummaryInput(handle) {
    if (!cartForm) return;
    cartForm.querySelectorAll('input[name^="attributes["]').forEach(function (input) {
      var key = input.name.slice('attributes['.length, -1);
      if (isDestinationSummaryKey(key)) handle(input);
    });
  }

  // AJAXカート経路でも消えるようにする補助。失敗しても購入は止めない。
  /**
   * 複数配送をやめた/使えなくなったカートから、マルチシップの痕跡を全部落とす。
   *
   * 以前はお届け先サマリーしか消しておらず、`_multiship_data` と見積もりトークンが
   * 残った。その状態でウォレット決済に進むと、画面上は単一配送なのに
   * 見ず知らずの旧宛先へ子注文が作られ、複数配送分の送料まで請求されてしまう。
   * 行プロパティの `_multiship_quote_id` も残すと古いトークンが次のカートを
   * 認証しかねないので、明細ごと洗う。
   */
  var MULTISHIP_CART_ATTRIBUTE_KEYS = [
    '_multiship_data',
    '_multiship_carrier_ready',
    '_multiship_quote_id',
    '_multiship_quote_amount',
    '_multiship_quote_currency',
    DRAFT_ATTRIBUTE_KEY
  ];

  function clearMultishipCart() {
    draftExists = false;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(assertOk)
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var attributes = {};
        Object.keys(cart.attributes || {}).forEach(function (key) {
          if (isDestinationSummaryKey(key)) attributes[key] = '';
        });
        MULTISHIP_CART_ATTRIBUTE_KEYS.forEach(function (key) {
          if ((cart.attributes || {})[key]) attributes[key] = '';
        });

        var staleLines = (cart.items || []).filter(function (item) {
          return item.properties && item.properties._multiship_quote_id;
        });
        if (Object.keys(attributes).length === 0 && staleLines.length === 0) return null;

        var chain = Object.keys(attributes).length
          ? fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ attributes: attributes })
            }).then(assertOk)
          : Promise.resolve(null);

        // 行番号は削除でずれるので、後ろから順に処理する。
        for (var i = cart.items.length - 1; i >= 0; i--) {
          (function (index, item) {
            if (!item.properties || !item.properties._multiship_quote_id) return;
            var properties = copyProperties(item.properties);
            // /cart/change.js は渡さなかったキーを消さない。空文字を渡すのが削除の作法。
            properties._multiship_quote_id = '';
            chain = chain.then(function () {
              return fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                  line: index + 1,
                  quantity: item.quantity,
                  properties: properties
                })
              }).then(assertOk);
            });
          })(i, cart.items[i]);
        }
        return chain;
      })
      .catch(function (err) {
        console.warn('[multiship] カートのマルチシップ状態の消去に失敗:', err);
      });
  }

  function setCartAttributeInput(key, value) {
    if (!cartForm) return;
    var selector = 'input[name="attributes[' + cssEscape(key) + ']"]';
    var input = cartForm.querySelector(selector);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'attributes[' + key + ']';
      cartForm.appendChild(input);
    }
    input.value = value;
  }

  function copyProperties(properties) {
    var copy = {};
    Object.keys(properties || {}).forEach(function (key) {
      if (properties[key] !== null && properties[key] !== undefined) {
        copy[key] = properties[key];
      }
    });
    return copy;
  }

  function assertOk(response) {
    if (!response.ok) throw new Error('cart_update_failed');
    return response;
  }

  function setSubmitting(isSubmitting) {
    if (!cartForm) return;
    var buttons = cartForm.querySelectorAll('button[type="submit"], input[type="submit"], button[name="checkout"], input[name="checkout"]');
    buttons.forEach(function (button) {
      button.disabled = isSubmitting;
    });
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/"/g, '\\"');
  }

  function esc(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // --- 初期化 ---
  //
  // 可否判定を先に出してから下書きを戻す。順番を逆にすると、複数配送が
  // 使えないカートで一瞬だけ入力欄が見えてしまう。
  init();

  function init() {
    var available = updateAvailability();

    if (!available) {
      // 使えないカートこそ掃除が要る。以前はここで即 return していたため、
      // 「/cart を開いた時点で既に複数配送不可」だと お届け先N が残ったまま
      // 決済でき、分割されない注文のメールに実在しない宛先が並び、
      // しかもLiquidが排他分岐なので本当の配送先住所が消えていた。
      if (container.getAttribute('data-has-multiship-state') === 'true') {
        clearMultishipCart();
        blankDestinationSummaryInputs();
      }
      setDynamicCheckoutBlocked(false);
      return;
    }

    if (restoreDraft()) {
      if (toggle) toggle.checked = true;
      if (panel) panel.hidden = false;
      setDynamicCheckoutBlocked(true);
      updateRemainingQty();
      renderDestinations();
      syncData();
    }

    // 描画時の data-* は古い可能性があるので、表示直後に実カートで裏取りする。
    refreshCartState();
  }
})();
